/** biome-ignore-all lint/suspicious/noExplicitAny: any is needed here */

import { useQuery } from '@apollo/client';
import type { UseDealTroubleshootingJobFetch } from '@openx/components-deal/lib/utils/context/DealHookTypes';
import { handleBqJobResponse } from '@openx/components-deal/lib/utils/handleBqJobResponse';
import { BQ_JOB_MAX_REQUEST_COUNT, BQ_JOB_REFETCH_INTERVAL } from '@openx/utils/services/config';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  GetDealTroubleshootingJobDocument,
  type GetDealTroubleshootingJobQuery,
} from '../../../../graphql/query/deals/GetDealTroubleshootingJob.generated';

export const useDealTroubleshootingJobFetch: UseDealTroubleshootingJobFetch = ({ job_id, deal_id, enabled = true }) => {
  const requestsCountRef = useRef<number>(1);
  const activeRequestRef = useRef({ dealId: deal_id, enabled, jobId: job_id });

  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<GetDealTroubleshootingJobQuery['dealTroubleshootingJob'] | undefined>();
  const [isLoading, setIsLoading] = useState<boolean>(false);

  activeRequestRef.current = { dealId: deal_id, enabled, jobId: job_id };

  const isCurrentRequest = useCallback(
    () =>
      activeRequestRef.current.dealId === deal_id &&
      activeRequestRef.current.jobId === job_id &&
      activeRequestRef.current.enabled,
    [deal_id, job_id]
  );

  const stateUpdateCallback = useCallback(
    ({
      errorMessage,
      data,
    }: {
      errorMessage?: string;
      data?: GetDealTroubleshootingJobQuery['dealTroubleshootingJob'];
    }) => {
      if (!isCurrentRequest()) {
        return;
      }
      if (errorMessage) {
        setError(errorMessage);
      }
      if (data) {
        setData(data);
      }
    },
    [isCurrentRequest]
  );

  const { startPolling, stopPolling } = useQuery(GetDealTroubleshootingJobDocument, {
    notifyOnNetworkStatusChange: true,
    onCompleted: responseData => {
      if (!isCurrentRequest()) {
        return;
      }

      const jobData = responseData.dealTroubleshootingJob;
      if (!jobData) {
        setError('No job data received');
        stopPolling();
        setIsLoading(false);
        return;
      }

      // Pass the full GraphQL response to handleBqJobResponse
      const mappedData: any = {
        status: jobData.status,
        output: jobData.output,
        // msg field not in generated types but exists in API
        msg: (jobData as any).msg,
      };

      handleBqJobResponse<GetDealTroubleshootingJobQuery['dealTroubleshootingJob']>({
        MAX_REQUEST_COUNT: BQ_JOB_MAX_REQUEST_COUNT,
        data: mappedData,
        reqCount: requestsCountRef.current,
        stateUpdateCallback,
        stopPolling: () => {
          if (isCurrentRequest()) {
            stopPolling();
            setIsLoading(false);
          }
        },
        updateReqCount: () => {
          if (isCurrentRequest()) {
            requestsCountRef.current += 1;
          }
        },
      });
    },
    onError: error => {
      if (!isCurrentRequest()) {
        return;
      }

      // Normalize error to string for display purposes
      const errorMessage = error instanceof Error ? error.message : String(error);
      setError(errorMessage);
      stopPolling();
      setIsLoading(false);
    },
    skip: !enabled,
    variables: { deal_id, job_id },
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: polling controls are stable
  useEffect(() => {
    //Reset state to initial
    setError(null);
    setData(undefined);
    requestsCountRef.current = 1;

    if (job_id && deal_id && enabled) {
      setIsLoading(true);
      startPolling(BQ_JOB_REFETCH_INTERVAL);
    }
    return () => stopPolling();
  }, [deal_id, enabled, job_id]);

  return { data, error, isLoading };
};
